import * as React from 'react'
import type { VendorSnapshot } from '@/lib/api'
import { lazyWithRetry } from '@/lib/lazyWithRetry'
import { selectPremiumTemplateId } from './templateId'

// ─── Lazy premium templates ──────────────────────────────────────────────────
// Each layout is its own chunk: a tenant only downloads the template they
// actually use (the render site wraps in <Suspense fallback={<PageSkeleton/>}>).
// Do NOT re-export the layout components (or anything else from their files)
// from this barrel — a static re-export would make the layout reachable from
// the eager graph and Rollup would silently merge the "lazy" chunk back in.
// `reloadOnFailure`: these are page-level chunks — after a redeploy invalidates
// old hashes, only a reload can recover (guarded against loops).

const PremiumLayout = React.lazy(
  lazyWithRetry(() => import('./PremiumLayout').then((m) => ({ default: m.PremiumLayout })), {
    reloadOnFailure: true,
  }),
)
const ModernLayout = React.lazy(
  lazyWithRetry(() => import('./modern/ModernLayout').then((m) => ({ default: m.ModernLayout })), {
    reloadOnFailure: true,
  }),
)
const LuxeLayout = React.lazy(
  lazyWithRetry(() => import('./luxe/LuxeLayout').then((m) => ({ default: m.LuxeLayout })), {
    reloadOnFailure: true,
  }),
)
const EditorialLayout = React.lazy(
  lazyWithRetry(
    () => import('./editorial/EditorialLayout').then((m) => ({ default: m.EditorialLayout })),
    { reloadOnFailure: true },
  ),
)

// ─── Template selector ───────────────────────────────────────────────────────
export { selectPremiumTemplateId } from './templateId'
export type { PremiumTemplateId } from './templateId'
export type { PremiumTrip, Brand, Vendor, MiniTrip } from './types'

/**
 * Pick the premium template component based on `snapshot.tierConfig.templateId`.
 *
 * Operators set this field via TripzOperator admin; the publishing pipeline
 * writes it into the vendor snapshot. Switching is live — no redeploy.
 * Default is `'classic'` so existing operators are unaffected until they
 * explicitly choose another template. Registering a new template here is
 * step 1 of 7 — see WIRING_MAP.md "Mini-site templates" for the full list.
 */
export function selectPremiumTemplate(
  snapshot: VendorSnapshot,
): React.ComponentType<{ snap: VendorSnapshot }> {
  switch (selectPremiumTemplateId(snapshot)) {
    case 'editorial':
      return EditorialLayout
    case 'luxe':
      return LuxeLayout
    case 'modern':
      return ModernLayout
    default:
      return PremiumLayout
  }
}
